import { useState } from "react";
import { AudioOverview } from "./AudioOverview";
import { CATEGORY_COLORS } from "./StoryCard";
import { StoryLink } from "./StoryLink";

export const LeadStory = ({ article, onClick }) => {
  const [imageError, setImageError] = useState(false);

  if (!article) return null;

  // Category mapping wins over the stored color — older articles carry the
  // pre-spec (inverted) colors baked in at ingestion.
  const color = CATEGORY_COLORS[article.category] || article.color || "#3A86D4";
  const hasValidImage = article.image_url && !imageError;

  const storyPath = `/story/${article.id}`;

  return (
    <div
      className="lead-story lead-grid grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-9 mb-11 pb-11 border-b border-scoop-border fade-in group"
      data-testid="lead-story"
    >
      {/* Visual — self-start stops the grid's default stretch from forcing the
          image taller than 4:3 whenever the content column grows (adding the
          audio card was visibly distorting the hero photo's crop).

          aria-hidden + tabIndex -1: this links to the same place as the
          headline below, so exposing it would give keyboard and screen-reader
          users two identical stops for one story. The headline link is the
          one that carries the accessible name. */}
      <StoryLink
        to={storyPath}
        onActivate={onClick}
        aria-hidden="true"
        tabIndex={-1}
        className="bg-[#EEF5FF] rounded-lg aspect-[4/3] self-start w-full flex items-center justify-center overflow-hidden"
        data-testid="lead-visual"
      >
        {hasValidImage ? (
          <img
            src={article.image_url}
            alt={article.headline || article.title}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{
            background: `linear-gradient(135deg, #EEF5FF 0%, ${color}15 100%)`
          }}>
            <span className="text-7xl opacity-80" aria-hidden="true">{article.emoji || "📰"}</span>
          </div>
        )}
      </StoryLink>

      {/* Content */}
      <div className="flex flex-col justify-center gap-3.5">
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: color }}
          />
          <span
            className="text-[0.72rem] font-bold tracking-widest uppercase"
            style={{ color }}
          >
            {article.sub_category || article.category}
          </span>
        </div>

        {/* The story link stops here: the audio player below has its own
            buttons and a seek slider, and nesting those inside an anchor is
            invalid HTML — which is exactly why the old markup needed a
            stopPropagation() workaround on the player. */}
        <StoryLink
          to={storyPath}
          onActivate={onClick}
          className="block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-scoop-blue focus-visible:ring-offset-2"
        >
          <h2
            className="lead-headline font-display font-black text-[1.7rem] md:text-[2.1rem] lg:text-[2.4rem] leading-[1.15] tracking-tight text-scoop-ink transition-colors group-hover:text-scoop-blue"
            data-testid="lead-headline"
          >
            {article.headline}
          </h2>

          <p className="text-[0.9rem] md:text-base text-gray-600 leading-relaxed tracking-[-0.005em] mt-3.5">
            {article.deck}
          </p>

          <div className="text-[0.75rem] text-scoop-muted font-semibold tracking-wide mt-3.5">
            Written with AI · {article.read_time} min read
          </div>
        </StoryLink>

        {/* Audio preview — onReadStory opens the same story modal as every
            other homepage tap, so the funnel stays in one nav paradigm */}
        <AudioOverview article={article} mode="preview" onReadStory={onClick} />
      </div>
    </div>
  );
};

export default LeadStory;
